// assets/src/components/ManageReservation.js
//
// Customer-facing "manage your reservation" flow. Lives inside the same
// modal shell as the booking funnel but has its own panels (rendered by
// templates/steps/step-manage-*.php) and its own state machine.
//
// State graph:
//   idle ──open()──► entry ──submit──► authenticating ──ok──► editing
//                       ▲                  │  fail
//                       └───── retry ──────┘
//   editing ──save()──► saving ──ok──► success
//                          │  401-on-stale-token
//                          └─► silent re-auth ─► saving (one retry)
//                          │  fail
//                          └─► editing (with error banner)
//
// Coordination with BookingForm:
//   - We don't reuse the booking funnel's step-1..4 panels; we toggle our
//     own [hidden] panels and tell BookingForm which DOMs to hide on open.
//   - The shared X close button in the modal header routes to our
//     handler when manage mode is active (booking flow's close handler
//     defers via the `_manageActive` flag set on the BookingForm instance).
//   - Network gates and hold timers from the booking flow stay parked
//     while manage mode is active: BookingForm._allowNetwork is left
//     false so the calendar fetcher doesn't fire.

import {
    authenticate,
    update,
    canonicalizeCode,
    ManageAuthError,
    ManageUpdateError,
} from '../api/manage.js';
import { initScrollAffordances } from './ScrollAffordance.js';

const naluDebug = (...args) => {
    if (typeof window !== 'undefined' && window.naluBookingVars?.debug) {
        // eslint-disable-next-line no-console
        console.log('[manage]', ...args);
    }
};

// Sentinel meal values that nudge the customer to pick a real choice.
// Compared case-insensitively; round-tripped verbatim on save.
const MEAL_SENTINELS = new Set(['pending', 'none']);

const isSentinelMeal = (value) =>
    typeof value === 'string' && MEAL_SENTINELS.has(value.trim().toLowerCase());

// Save-button label shown when the auth response returns an empty
// `editable` array — the online-change window has closed.
const SAVE_LABEL_CLOSED =
    'Window for online changes has closed — please contact us and we can try to accommodate last-minute changes.';

// Save-button label when the form has no unsaved edits.
const SAVE_LABEL_NO_CHANGES = 'No Changes to Save';

// -- Body scroll lock ---------------------------------------------------------
//
// Same shape as the booking flow's lockBody/unlockBody — duplicated rather
// than imported because BookingForm's helpers are scoped inside the
// NaluBookingUx IIFE. Keep the two in sync if either side changes.
let _bodyScrollY = 0;
function lockBody() {
    _bodyScrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${_bodyScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
}
function unlockBody() {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, _bodyScrollY || 0);
}

export class ManageReservation {
    constructor() {
        this.bookingJs = null;

        // State machine — see header comment for the graph.
        this.state = 'idle';

        // Resolved reservation data (set after authenticate succeeds).
        this.reservation = null;
        this.sessionToken = null;
        this.editable = [];

        // Minutes-before-departure cutoff for online changes, plus the
        // derived flag set in _renderEditScreen when that window has
        // passed (auth returned an empty `editable`).
        this.cutoff = null;
        this._changesClosed = false;

        // Last code the user submitted — kept in memory so the update
        // path can transparently re-auth on a 401 without prompting.
        this._lastCode = '';

        // Snapshot of passenger field values at the moment we entered
        // editing state, used to detect dirty state on close.
        this._initialFormState = null;

        // Confirm button + its label, cached by _wireEdit. The default
        // label text is captured so _setState can restore it after the
        // "Saving…" swap without hardcoding the string twice.
        this.saveBtn = null;
        this.saveBtnLabel = null;
        this.saveBtnDefaultText = 'Confirm & Save Changes';
    }

    init() {
        // Lazy DOM cache: panels are rendered server-side and exist for
        // the lifetime of the page, so caching once on init is fine.
        this.panels = {
            entry:    document.getElementById('nalu-manage-entry'),
            loading:  document.getElementById('nalu-manage-loading'),
            edit:     document.getElementById('nalu-manage-edit'),
            success:  document.getElementById('nalu-manage-success'),
        };
        this.discardDialog = document.getElementById('nalu-manage-discard-dialog');
        this.paxTemplate = document.getElementById('nalu-manage-pax-template');

        // Bail quietly if the plugin's modal isn't on this page (the
        // shortcode was disabled, or the footer auto-inject didn't run).
        if (!this.panels.entry) return;

        this._wireEntry();
        this._wireEdit();
        this._wireSuccess();
        this._wireDiscardDialog();
    }

    /** Called by index.js after BookingForm has been constructed. */
    bind(bookingJs) {
        this.bookingJs = bookingJs;
    }

    // -- State entry helpers (each one updates `this.state` + DOM) ----------

    _setState(next) {
        naluDebug('state', this.state, '→', next);
        this.state = next;

        for (const [key, el] of Object.entries(this.panels)) {
            if (!el) continue;
            // 'authenticating' shares the loading panel; 'saving' overlays the
            // edit screen with a busy class so the user keeps tour-info
            // context. Otherwise one panel per state.
            const map = {
                entry: 'entry',
                authenticating: 'loading',
                editing: 'edit',
                saving: 'edit',
                success: 'success',
            };
            const visiblePanel = map[next];
            if (key === visiblePanel) {
                el.hidden = false;
            } else {
                el.hidden = true;
            }
        }

        // Busy class on edit panel during 'saving' to disable inputs visually.
        if (this.panels.edit) {
            this.panels.edit.classList.toggle('is-busy', next === 'saving');
        }

        // Confirm button label / disabled / styling is a pure function
        // of closed-window, saving and dirty state — see _syncSaveButton.
        this._syncSaveButton();
    }

    /**
     * Recompute the Confirm button from the current conditions. Called on
     * every state transition and on every editable-field change.
     *   - closed window → static contact notice (highest priority)
     *   - saving        → spinner + "Saving…"
     *   - no edits yet  → disabled, greyed, "No Changes to Save"
     *   - has edits     → enabled, "Confirm & Save Changes"
     */
    _syncSaveButton() {
        if (!this.saveBtn || !this.saveBtnLabel) return;
        const saving = this.state === 'saving';
        this.saveBtn.classList.toggle('is-loading', saving);

        if (this._changesClosed) {
            this.saveBtn.disabled = true;
            this.saveBtn.classList.add('is-closed');
            this.saveBtn.classList.remove('is-unchanged');
            this.saveBtnLabel.textContent = SAVE_LABEL_CLOSED;
            return;
        }
        this.saveBtn.classList.remove('is-closed');

        if (saving) {
            this.saveBtn.disabled = true;
            this.saveBtn.classList.remove('is-unchanged');
            this.saveBtnLabel.textContent = 'Saving…';
            return;
        }

        const dirty = this._isDirty();
        this.saveBtn.disabled = !dirty;
        this.saveBtn.classList.toggle('is-unchanged', !dirty);
        this.saveBtnLabel.textContent = dirty ? this.saveBtnDefaultText : SAVE_LABEL_NO_CHANGES;
    }

    /**
     * Open the modal in manage mode. If `code` is a non-empty canonical
     * string we run auth immediately; otherwise we land on the manual
     * entry step.
     */
    open(code = '') {
        if (!this.panels.entry) return;

        // Hide booking-flow panels so the modal shell only shows ours.
        this._hideBookingPanels();

        // Mark the modal as in manage mode so the shared X close button
        // routes to our handler.
        if (this.bookingJs) this.bookingJs._manageActive = true;

        // Open the modal shell.
        const root = document.querySelector('.nalu-booking-modal');
        if (root) root.classList.add('open');
        document.body.classList.add('nalu-modal-open');
        lockBody();

        // Wire the "more below" scroll cues — the deep-link manage flow can
        // open the modal without going through BookingForm.openModal.
        requestAnimationFrame(() => initScrollAffordances());

        // Route based on whether we got a code from the URL.
        const canonical = canonicalizeCode(code);
        if (canonical) {
            this._submitCode(canonical);
        } else {
            this._showEntry({ error: null, prefill: '' });
        }
    }

    _hideBookingPanels() {
        if (!this.bookingJs?.els) return;
        const { step1, step2, step3, step4 } = this.bookingJs.els;
        [step1, step2, step3, step4].forEach((el) => {
            if (el) el.classList.remove('active');
        });
    }

    /**
     * Close the modal and reset manage state. Goes through the dirty
     * check unless `force` — used both for the user-initiated close
     * (via the X button or success Done) and for programmatic teardown.
     */
    requestClose() {
        if (this.state === 'success') {
            // Done from success screen → close + nav home (no back history
            // since we entered via email link).
            this._teardownAndClose();
            window.location.href = '/';
            return;
        }

        if (this._isDirty()) {
            this._openDiscardDialog();
            return;
        }

        this._teardownAndClose();
    }

    _teardownAndClose() {
        // Reset internal state regardless of which panel we were on.
        this.reservation = null;
        this.sessionToken = null;
        this.editable = [];
        this._lastCode = '';
        this._initialFormState = null;

        const root = document.querySelector('.nalu-booking-modal');
        if (root) root.classList.remove('open');
        document.body.classList.remove('nalu-modal-open');
        unlockBody();

        // Hide all our panels and re-show step-1 so the modal is ready
        // for the booking flow on its next open.
        Object.values(this.panels).forEach((el) => { if (el) el.hidden = true; });
        if (this.bookingJs?.els?.step1) {
            this.bookingJs.els.step1.classList.add('active');
        }
        if (this.bookingJs) this.bookingJs._manageActive = false;

        this.state = 'idle';
    }

    // -- Entry step ---------------------------------------------------------

    _wireEntry() {
        const form = this.panels.entry?.querySelector('[data-manage-entry-form]');
        if (!form) return;

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            const input = form.querySelector('#nalu-manage-code');
            const raw = input?.value || '';
            const canonical = canonicalizeCode(raw);
            if (!canonical) {
                this._setEntryError('Please enter your reservation code.');
                input?.focus();
                return;
            }
            this._submitCode(canonical);
        });
    }

    _showEntry({ error = null, prefill = '' } = {}) {
        // Undo a prior _showDisallowed: restore the code form, hide the CTA.
        // Re-entry (modal reopen, retype) always lands on the live form.
        this._toggleDisallowed(false);
        const input = this.panels.entry.querySelector('#nalu-manage-code');
        if (input) {
            input.value = prefill;
            // Defer focus until after the panel becomes visible.
            requestAnimationFrame(() => input.focus());
        }
        this._setEntryError(error);
        this._setState('entry');
    }

    // Show the "call the office" CTA on the entry screen and hide the code
    // form — used when the portal reports the reservation can't be managed
    // online (MANAGE_DISALLOWED). No prefill/focus: there's nothing to retype.
    _showDisallowed() {
        this._setEntryError(null);
        this._toggleDisallowed(true);
        this._setState('entry');
    }

    // Toggle entry-screen between the code form (default) and the contact CTA.
    _toggleDisallowed(showCta) {
        const cta = this.panels.entry?.querySelector('[data-manage-disallowed]');
        const form = this.panels.entry?.querySelector('[data-manage-entry-form]');
        if (cta) cta.hidden = !showCta;
        if (form) form.hidden = showCta;
    }

    _setEntryError(message) {
        const banner = this.panels.entry.querySelector('[data-manage-error]');
        const text = this.panels.entry.querySelector('[data-error-message]');
        if (!banner) return;
        if (message) {
            if (text) text.textContent = message;
            banner.hidden = false;
        } else {
            banner.hidden = true;
        }
    }

    async _submitCode(canonicalCode) {
        this._lastCode = canonicalCode;
        this._setState('authenticating');

        try {
            const res = await authenticate(canonicalCode);
            this.reservation = res.reservation;
            this.sessionToken = res.sessionToken;
            this.editable = Array.isArray(res.editable) ? res.editable : [];
            this.cutoff = typeof res.cutoff === 'number' ? res.cutoff : null;
            this._renderEditScreen();
            this._setState('editing');
        } catch (err) {
            console.warn('Manage reservation auth failed', err);
            // This reservation has online management disabled. Retyping won't
            // help — swap the code form for a "call the office" CTA instead of
            // the retype banner.
            if (err instanceof ManageAuthError && err.kind === 'manage_disallowed') {
                this._showDisallowed();
                return;
            }
            const message =
                err instanceof ManageAuthError
                    ? err.message
                    : "Sorry, something went wrong. Please try again.";
            // Send the user back to the entry screen so they can retype.
            // Prefill with their last attempt — if they fat-fingered one
            // character, retyping the whole 6-char code is friction.
            this._showEntry({ error: message, prefill: this._lastCode });
        }
    }

    // -- Edit screen --------------------------------------------------------

    _wireEdit() {
        this.saveBtn = this.panels.edit?.querySelector('[data-manage-save]') ?? null;
        this.saveBtnLabel = this.saveBtn?.querySelector('[data-manage-save-label]') ?? null;
        if (this.saveBtnLabel?.textContent) {
            this.saveBtnDefaultText = this.saveBtnLabel.textContent;
        }
        if (this.saveBtn) {
            this.saveBtn.addEventListener('click', () => this._save());
        }
    }

    _renderEditScreen() {
        const r = this.reservation;
        const root = this.panels.edit;
        if (!r || !root) return;

        // Clear any stale save-error banner from a prior modal session.
        this._setEditError(null);

        const setText = (sel, value) => {
            const el = root.querySelector(sel);
            if (el) el.textContent = value ?? '';
        };
        const setHref = (sel, value) => {
            const el = root.querySelector(sel);
            if (el) el.setAttribute('href', value);
        };

        setText('[data-manage-reservation-id]', r.id != null ? `#${r.id}` : '');
        setText('[data-manage-tour-name]', r.tour?.name);
        setText('[data-manage-tour-boat]', r.tour?.boat);
        setText('[data-manage-departure-when]', this._formatDeparture(r.departure?.datetime));
        setText('[data-manage-checkin-time]', r.departure?.checkInTime);
        setText('[data-manage-departure-location]', r.departure?.location);

        const phone = r.charter?.phone || '';
        setText('[data-manage-charter-phone]', phone);
        setHref('[data-manage-charter-phone-href]', `tel:${phone.replace(/[^\d+]/g, '')}`);
        const email = r.charter?.email || '';
        setText('[data-manage-charter-email]', email);
        setHref('[data-manage-charter-email-href]', `mailto:${email}`);

        // Online-change cutoff note, floored to whole hours.
        const cutoffEl = root.querySelector('[data-manage-cutoff]');
        if (cutoffEl) {
            const hours = Number.isFinite(this.cutoff) ? Math.floor(this.cutoff / 60) : 0;
            if (hours > 0) {
                cutoffEl.textContent =
                    `Changes can be made up to ${hours} ${hours === 1 ? 'hour' : 'hours'} prior to departure.`;
                cutoffEl.hidden = false;
            } else {
                cutoffEl.hidden = true;
            }
        }

        const list = root.querySelector('[data-manage-pax-list]');
        if (list) {
            list.innerHTML = '';
            const passengers = Array.isArray(r.passengers) ? r.passengers : [];
            const adultCount = passengers.filter((p) => p.type === 'adult').length;
            let adultIdx = 0;
            let childIdx = 0;
            for (const pax of passengers) {
                let heading;
                if (pax.type === 'child') {
                    childIdx += 1;
                    heading = `Child ${childIdx}`;
                } else {
                    adultIdx += 1;
                    heading = adultIdx === 1 && pax.isPrimary
                        ? 'Adult 1 & Primary Contact Information'
                        : (adultCount > 1 ? `Adult ${adultIdx}` : 'Adult');
                }
                list.appendChild(this._renderPaxRow(pax, heading));
            }
        }

        // When auth returns an empty `editable`, the online-change window
        // has closed. _syncSaveButton (via the _setState('editing') that
        // follows render) turns the Confirm button into a static notice.
        this._changesClosed = !this.editable.includes('lunches');

        // Snapshot current state so dirty-detection has a baseline.
        this._initialFormState = this._captureFormState();

        this._refreshSummary();
    }

    _renderPaxRow(pax, heading) {
        const frag = this.paxTemplate.content.cloneNode(true);
        const row = frag.querySelector('[data-manage-pax]');
        row.dataset.paxId = pax.id;

        row.querySelector('[data-pax-heading]').textContent = heading;

        const nameInput = row.querySelector('[data-pax-name]');
        nameInput.value = pax.fullName || '';
        // Phase-1 policy is lunches only; names stay disabled. When
        // `passenger_names` joins editable, flip this gate.
        const namesEditable = this.editable.includes('passenger_names');
        nameInput.disabled = !namesEditable;
        // Hide the asterisk if names are read-only — visual cue that the
        // field isn't expecting input from the customer right now.
        const required = row.querySelector('[data-pax-name-required]');
        if (required && !namesEditable) required.hidden = true;

        const lunchSelect = row.querySelector('[data-pax-lunch]');
        const lunchEditable = this.editable.includes('lunches');
        lunchSelect.disabled = !lunchEditable;
        this._populateLunchOptions(lunchSelect, pax.lunch);
        // Apply red-mark styling when the current value is a sentinel.
        // Style class follows the [.is-unset] convention used elsewhere
        // for "needs attention" form fields in this plugin.
        this._refreshLunchSentinelMark(lunchSelect);
        lunchSelect.addEventListener('change', () => {
            this._refreshLunchSentinelMark(lunchSelect);
            // Pending-lunch count + Confirm button track edits live.
            this._refreshSummary();
            this._syncSaveButton();
        });

        const noteInput = row.querySelector('[data-pax-note]');
        noteInput.value = pax.mealNote || '';
        noteInput.disabled = !lunchEditable; // notes ride with the lunch policy
        // 200-char limit enforced by maxlength in template.
        noteInput.addEventListener('input', () => this._syncSaveButton());

        const waiverEl = row.querySelector('[data-pax-waiver]');
        if (waiverEl) {
            if (pax.waiverSigned) {
                // Signed — nothing to show.
                waiverEl.remove();
            } else {
                const link = waiverEl.querySelector('[data-pax-waiver-link]');
                if (link && pax.waiverUrl) {
                    link.href = pax.waiverUrl;
                    link.addEventListener('click', (e) => {
                        if (link.classList.contains('is-disabled')) {
                            e.preventDefault();
                            return;
                        }
                        // First click navigates normally (new tab). We
                        // can't confirm the waiver is signed until its
                        // webhook fires and the reservation is re-opened,
                        // so disable the CTA for the rest of this session.
                        link.classList.add('is-disabled');
                        link.setAttribute('aria-disabled', 'true');
                    });
                } else if (link) {
                    // Unsigned but no destination — drop the dead link.
                    link.remove();
                }
            }
        }

        return row;
    }

    /**
     * Recompute the readiness summary above Passenger Information:
     * unsigned waivers + lunches still on a sentinel value. Waiver state
     * is fixed for the session; lunch state is live, so this also runs
     * on every lunch <select> change.
     */
    _refreshSummary() {
        const root = this.panels.edit;
        if (!root) return;

        const passengers = Array.isArray(this.reservation?.passengers)
            ? this.reservation.passengers
            : [];
        const unsignedWaivers = passengers.filter((p) => !p.waiverSigned).length;

        let pendingLunches = 0;
        for (const sel of root.querySelectorAll('[data-pax-lunch]')) {
            if (isSentinelMeal(sel.value)) pendingLunches += 1;
        }

        const readyEl    = root.querySelector('[data-manage-summary-ready]');
        const issuesEl   = root.querySelector('[data-manage-summary-issues]');
        const waiversLi  = root.querySelector('[data-manage-summary-waivers]');
        const lunchesLi  = root.querySelector('[data-manage-summary-lunches]');
        const waiverNote = root.querySelector('[data-manage-summary-waiver-note]');

        const allSet = unsignedWaivers === 0 && pendingLunches === 0;
        if (readyEl)  readyEl.hidden  = !allSet;
        if (issuesEl) issuesEl.hidden = allSet;

        if (waiversLi) {
            waiversLi.hidden = unsignedWaivers === 0;
            waiversLi.textContent = unsignedWaivers === 1
                ? '1 waiver still needs signing'
                : `${unsignedWaivers} waivers still need signing`;
        }
        if (waiverNote) waiverNote.hidden = unsignedWaivers === 0;
        if (lunchesLi) {
            lunchesLi.hidden = pendingLunches === 0;
            lunchesLi.textContent = pendingLunches === 1
                ? '1 lunch selection is still pending'
                : `${pendingLunches} lunch selections are still pending`;
        }
    }

    _populateLunchOptions(selectEl, currentValue) {
        const meals = Array.isArray(this.reservation?.availableMeals)
            ? this.reservation.availableMeals
            : [];

        // If current value is a sentinel ("Pending"/"None") it isn't in
        // availableMeals — prepend it so the dropdown can render the
        // current state, and we don't silently change the value just
        // by rendering.
        const options = [];
        if (currentValue && !meals.includes(currentValue)) {
            options.push(currentValue);
        }
        for (const meal of meals) options.push(meal);

        selectEl.innerHTML = '';
        for (const opt of options) {
            const optionEl = document.createElement('option');
            optionEl.value = opt;
            optionEl.textContent = opt;
            if (opt === currentValue) optionEl.selected = true;
            selectEl.appendChild(optionEl);
        }
    }

    _refreshLunchSentinelMark(selectEl) {
        selectEl.classList.toggle('is-unset', isSentinelMeal(selectEl.value));
    }

    _formatDeparture(iso) {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            // Format in the tour's timezone (HST) — not the customer's
            // browser TZ. The ISO string carries the offset, but
            // toLocaleDateString without `timeZone` re-renders in the
            // browser's TZ, so an HST 8:30 AM tour shows as 11:30 AM
            // for an MST customer. Hardcoding HST matches the rest of
            // the codebase (Pacific/Honolulu, see BookingForm.js); this
            // becomes dynamic when the portal carries tour-local TZ.
            const tz = 'Pacific/Honolulu';
            const date = d.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: tz,
            });
            const time = d.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: tz,
            });
            return `${date} at ${time}`;
        } catch {
            return iso;
        }
    }

    // -- Save flow ----------------------------------------------------------

    async _save() {
        if (this.state !== 'editing') return;

        const payload = this._buildUpdatePayload();
        if (!payload) {
            // No-op save: pretend success so the user gets the closure
            // signal they expect, but don't burn a network call.
            this._setState('success');
            return;
        }

        this._setEditError(null);
        this._setState('saving');
        try {
            await update(this.sessionToken, payload);
            this._initialFormState = this._captureFormState();
            this._setState('success');
        } catch (err) {
            console.warn('Manage reservation update failed', err);
            if (err instanceof ManageUpdateError && err.kind === 'session_expired') {
                // Try a single transparent re-auth using the durable code.
                try {
                    const res = await authenticate(this._lastCode);
                    this.sessionToken = res.sessionToken;
                    await update(this.sessionToken, payload);
                    this._initialFormState = this._captureFormState();
                    this._setState('success');
                    return;
                } catch (reAuthErr) {
                    console.warn('Re-auth after expired session failed', reAuthErr);
                    // Fall through to send them back to entry.
                    this._showEntry({
                        error: "Your session expired. Please re-enter your code.",
                        prefill: this._lastCode,
                    });
                    return;
                }
            }
            // Generic failure — keep them on the edit screen so their
            // changes aren't lost, and surface an inline banner rather
            // than a browser alert.
            this._setState('editing');
            this._setEditError(
                "We couldn't save your changes. Please try again later, or contact us if it's urgent.",
            );
        }
    }

    /**
     * Show or clear the inline save-error banner on the edit screen.
     * Pass a falsy message to hide it.
     */
    _setEditError(message) {
        const banner = this.panels.edit?.querySelector('[data-manage-edit-error]');
        if (!banner) return;
        if (message) {
            banner.textContent = message;
            banner.hidden = false;
            // The banner lives in the tour card — on mobile that's well
            // above the fold once the customer has scrolled down to the
            // passenger fields, so a failed save would otherwise be
            // announced off-screen. Pull it into view. rAF so the layout
            // settles after the panel is un-hidden by the state change.
            requestAnimationFrame(() => {
                banner.scrollIntoView({ block: 'center', behavior: 'smooth' });
            });
        } else {
            banner.hidden = true;
        }
    }

    _buildUpdatePayload() {
        const rows = this.panels.edit.querySelectorAll('[data-manage-pax]');
        const initial = this._initialFormState || { passengers: {} };

        const mutated = [];
        for (const row of rows) {
            const id = row.dataset.paxId;
            const lunch = row.querySelector('[data-pax-lunch]')?.value ?? '';
            const mealNote = row.querySelector('[data-pax-note]')?.value ?? '';

            const baseline = initial.passengers?.[id] ?? { lunch: '', mealNote: '' };
            const diff = { id };
            let changed = false;
            if (lunch !== baseline.lunch) {
                diff.lunch = lunch;
                changed = true;
            }
            if (mealNote !== baseline.mealNote) {
                diff.mealNote = mealNote;
                changed = true;
            }
            if (changed) mutated.push(diff);
        }

        if (mutated.length === 0) return null;
        return {
            reservationId: this.reservation?.id,
            passengers: mutated,
        };
    }

    // -- Dirty tracking + discard dialog ------------------------------------

    _captureFormState() {
        const rows = this.panels.edit.querySelectorAll('[data-manage-pax]');
        const passengers = {};
        for (const row of rows) {
            const id = row.dataset.paxId;
            passengers[id] = {
                lunch: row.querySelector('[data-pax-lunch]')?.value ?? '',
                mealNote: row.querySelector('[data-pax-note]')?.value ?? '',
            };
        }
        return { passengers };
    }

    _isDirty() {
        if (this.state !== 'editing' && this.state !== 'saving') return false;
        const initial = this._initialFormState;
        if (!initial) return false;
        const current = this._captureFormState();
        for (const [id, fields] of Object.entries(current.passengers)) {
            const baseline = initial.passengers[id];
            if (!baseline) return true;
            if (baseline.lunch !== fields.lunch) return true;
            if (baseline.mealNote !== fields.mealNote) return true;
        }
        return false;
    }

    _wireDiscardDialog() {
        if (!this.discardDialog) return;
        this.discardDialog.querySelectorAll('[data-manage-discard-cancel]').forEach((btn) => {
            btn.addEventListener('click', () => {
                this.discardDialog.classList.remove('open');
                try { this.discardDialog.close?.(); } catch {}
            });
        });
        this.discardDialog.querySelectorAll('[data-manage-discard-confirm]').forEach((btn) => {
            btn.addEventListener('click', () => {
                this.discardDialog.classList.remove('open');
                try { this.discardDialog.close?.(); } catch {}
                this._teardownAndClose();
            });
        });
    }

    _openDiscardDialog() {
        if (!this.discardDialog) {
            // Fall back to a synchronous confirm if the dialog markup is
            // missing — better to ask than to silently lose the user's
            // edits.
            // eslint-disable-next-line no-alert
            if (window.confirm('Discard your changes?')) {
                this._teardownAndClose();
            }
            return;
        }
        this.discardDialog.classList.add('open');
        try { this.discardDialog.showModal?.(); } catch {}
    }

    // -- Success step -------------------------------------------------------

    _wireSuccess() {
        const doneBtn = this.panels.success?.querySelector('[data-manage-done]');
        if (doneBtn) {
            doneBtn.addEventListener('click', () => this.requestClose());
        }
    }
}
